import { z } from 'zod';
/**
 * Standard HospaSense software modules available for commercial licensing.
 */
export const MODULE_KEYS = [
    'clinical_core',
    'billing_invoicing',
    'opd_ipd',
    'pharmacy_inventory',
    'lab_radiology',
    'abdm_bridge',
    'patient_portal',
    'analytics_reports',
];
export const ModuleKeySchema = z.string();
export const MODULE_DEFINITIONS = {
    clinical_core: {
        name: 'Clinical Core & EMR',
        description: 'Patient charts, encounter notes, diagnoses, and medical summaries.',
        clinical: true,
        core: true,
    },
    billing_invoicing: {
        name: 'Hospital Billing & Insurance',
        description: 'Hospital patient billing, tariff management, and claims processing.',
        clinical: false,
        core: true,
    },
    opd_ipd: {
        name: 'OPD & IPD Management',
        description: 'Bed allocation, ward management, appointment scheduling, and rounds.',
        clinical: true,
        core: true,
    },
    pharmacy_inventory: {
        name: 'Pharmacy & Stock Inventory',
        description: 'Drug formulary, dispensing, batches, and reorder alerts.',
        clinical: true,
        core: false,
    },
    lab_radiology: {
        name: 'Lab & Diagnostics (LIS/RIS)',
        description: 'Test ordering, specimen tracking, machine integration, and reports.',
        clinical: true,
        core: false,
    },
    abdm_bridge: {
        name: 'ABDM India Health Stack Bridge',
        description: 'ABHA creation, health record linking, and gateway compliance.',
        clinical: false,
        core: false,
    },
    patient_portal: {
        name: 'Patient Portal & Telehealth',
        description: 'Self-service patient appointment booking, lab download, and messaging.',
        clinical: false,
        core: false,
    },
    analytics_reports: {
        name: 'Executive Analytics & MIS',
        description: 'Operational dashboards, census metrics, revenue cycle intelligence.',
        clinical: false,
        core: false,
    },
};
export const CAPACITY_METRICS = [
    'licensed_beds',
    'active_doctors',
    'monthly_opd_visits',
    'storage_gb',
    'connected_devices',
];
export const CapacityMetricSchema = z.string();
export const CAPACITY_METRIC_LABELS = {
    licensed_beds: { label: 'Licensed Beds', unit: 'beds' },
    active_doctors: { label: 'Active Doctors', unit: 'practitioners' },
    monthly_opd_visits: { label: 'Monthly OPD Visits', unit: 'visits/mo' },
    storage_gb: { label: 'Cloud/Archive Storage', unit: 'GB' },
    connected_devices: { label: 'Connected Medical Devices', unit: 'devices' },
};
export const PLAN_KEYS = [
    'starter_clinic',
    'growth_hospital',
    'enterprise_multispecialty',
    'academic_medical_center',
];
export const PlanKeySchema = z.string();
export const CONTRACT_STATUSES = [
    'draft',
    'active',
    'past_due',
    'in_grace_period',
    'expired',
    'terminated',
];
export const ContractStatusSchema = z.enum(CONTRACT_STATUSES);
export const AMENDMENT_TYPES = [
    'plan_upgrade',
    'plan_downgrade',
    'capacity_expansion',
    'capacity_reduction',
    'module_add',
    'module_remove',
    'term_renewal',
    'discount_applied',
    'jurisdiction_update',
];
export const AmendmentTypeSchema = z.enum(AMENDMENT_TYPES);
export const BILLING_INTERVALS = ['monthly', 'quarterly', 'annual'];
export const BillingIntervalSchema = z.enum(BILLING_INTERVALS);
/**
 * Commercial plan definition.
 */
export const PlanDefinitionSchema = z.object({
    id: z.string(),
    key: PlanKeySchema,
    name: z.string(),
    description: z.string(),
    basePriceAnnualInr: z.number().int().nonnegative(),
    defaultModules: z.array(ModuleKeySchema),
    defaultCapacity: z.record(CapacityMetricSchema, z.number().int().nonnegative()),
    isCustom: z.boolean(),
});
/**
 * Capacity limit schema on a contract.
 */
export const ContractCapacityLimitSchema = z.object({
    metric: z.string(),
    limitValue: z.number().default(0),
    warningThresholdPercent: z.number().default(85),
    overagePermitted: z.boolean().default(true),
    overageUnitRateInr: z.number().default(0),
});
/**
 * Commercial Contract representation.
 */
export const ContractSchema = z.object({
    id: z.string(),
    organisationId: z.string(),
    organisationName: z.string().optional(),
    contractNumber: z.string(),
    planKey: PlanKeySchema,
    planName: z.string(),
    status: ContractStatusSchema,
    startDate: z.iso.datetime(),
    endDate: z.iso.datetime(),
    billingInterval: BillingIntervalSchema,
    currency: z.literal('INR'),
    annualContractValueInr: z.number().nonnegative(),
    discountPercent: z.number().min(0).max(100).default(0),
    jurisdictionStateCode: z.string(), // e.g. 'KA', 'MH', 'DL'
    gstin: z.string().nullable().optional(),
    autoRenew: z.boolean().default(true),
    renewalNoticeDays: z.number().int().default(30),
    gracePeriodDays: z.number().int().default(15),
    includedModules: z.array(ModuleKeySchema),
    capacityLimits: z.array(ContractCapacityLimitSchema),
    notes: z.string().nullable().optional(),
    signedByCustomerName: z.string().nullable().optional(),
    signedAt: z.iso.datetime().nullable().optional(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    version: z.number().int().min(1),
});
/**
 * Contract amendment event tracking change history.
 */
export const ContractAmendmentSchema = z.object({
    id: z.string(),
    contractId: z.string(),
    organisationId: z.string(),
    amendmentNumber: z.string(),
    amendmentType: AmendmentTypeSchema,
    effectiveDate: z.iso.datetime(),
    summary: z.string(),
    changes: z.record(z.string(), z.unknown()),
    financialDeltaInr: z.number().int(),
    approvedByUserId: z.string(),
    approvedByDisplayName: z.string().nullable().optional(),
    createdAt: z.iso.datetime(),
});
/**
 * Create contract payload schema.
 */
export const ContractCreateSchema = z
    .object({
    organisationId: z.string(),
    planKey: PlanKeySchema,
    startDate: z.iso.datetime(),
    endDate: z.iso.datetime(),
    billingInterval: BillingIntervalSchema.default('annual'),
    annualContractValueInr: z.number().int().nonnegative(),
    discountPercent: z.number().min(0).max(100).default(0),
    jurisdictionStateCode: z.string().min(2).max(2).default('KA'),
    gstin: z.string().max(20).optional(),
    autoRenew: z.boolean().default(true),
    renewalNoticeDays: z.number().int().default(30),
    gracePeriodDays: z.number().int().default(15),
    includedModules: z.array(ModuleKeySchema),
    capacityLimits: z.array(ContractCapacityLimitSchema),
    notes: z.string().max(2000).optional(),
    signedByCustomerName: z.string().max(200).optional(),
})
    .strict();
/**
 * Create amendment payload schema.
 */
export const ContractAmendmentCreateSchema = z
    .object({
    amendmentType: AmendmentTypeSchema,
    effectiveDate: z.iso.datetime(),
    summary: z.string().min(5).max(1000),
    changes: z.record(z.string(), z.unknown()),
    financialDeltaInr: z.number().int().default(0),
})
    .strict();
//# sourceMappingURL=commercial.js.map